import { css } from "@emotion/css";
import { Callout, Checkbox, ExternalLink, PopoverBasicHelp } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { ConfigurationMessages } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationMessages";
import type { TargetConfigurationResource } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/KubernetesAgentTargetConfigurationDialog";
import { LocalDevServerUrlHelper } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/LocalDevServerUrlHelper";
import { configurationContainerStyle, configurationFormFieldsStyle } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/Styles";
import { ConfigurationPageStorageClassHelperContent } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { required, Text } from "~/components/form/index";
type AdvancedTargetConfigurationPageProps = {
    model: TargetConfigurationResource;
    onModelChange: <K extends keyof TargetConfigurationResource>(model: Pick<TargetConfigurationResource, K>) => void;
    canModifyServerUrls: boolean;
    getFieldError: (fieldName: string) => string;
    usePersistentVolumeStorage: boolean;
    onUsePersistentVolumeStorageChanged: (value: boolean) => void;
    useOctopusPermissionsController: boolean;
    onUseOctopusPermissionsControllerChanged: (value: boolean) => void;
    requiresHaConfiguration: boolean;
    permissionsControllerEnabled: boolean;
    isGrpcHaEnabled: boolean;
};
export function AdvancedTargetConfigurationPage(props: AdvancedTargetConfigurationPageProps) {
    const { getFieldError } = props;
    const disabledBecauseHaIsNotSupported = props.requiresHaConfiguration && !props.isGrpcHaEnabled;
    return (<div className={configurationContainerStyle}>
            <div className={configurationFormFieldsStyle}>
                <div className={styles.sectionTitle}>
                    <p className={styles.sectionDescriptionStyles}>Install bundle components</p>
                </div>

                {/* Kubernetes Monitor Checkbox */}
                <>
                    <div className={styles.inputWithHelp}>
                        <Checkbox disabled={disabledBecauseHaIsNotSupported} noMargin={true} label={"Kubernetes monitor"} popover={<PopoverBasicHelp placement="right-start" description={<div>
                                            The Octopus Kubernetes monitor is required to get the <ExternalLink href="live-object-status">live status</ExternalLink> of resources in your cluster
                                        </div>}/>} note="Required for Kubernetes Live Object Status" value={!disabledBecauseHaIsNotSupported && props.model.IncludeMonitorHelmCommands} onChange={(newValue) => props.onModelChange({ IncludeMonitorHelmCommands: newValue })}/>
                    </div>
                    {disabledBecauseHaIsNotSupported && (<div className={styles.haCallout}>
                            <Callout type={"warning"} title={"Kubernetes monitor is not supported in HA clusters"}/>
                        </div>)}
                </>

                {/* Octopus Permissions Controller Checkbox */}
                {props.permissionsControllerEnabled && (<div className={styles.inputWithHelp}>
                        <Checkbox noMargin={true} label={"Permissions controller"} popover={<PopoverBasicHelp placement="right-start" description={<div>
                                            <b>Octopus Permissions Controller</b>
                                            <p>
                                                The Permissions Controller applies Kubernetes RBAC rules to deployment tasks executed by the Kubernetes agent, giving you granular control over permissions. Deployment tasks will run under a Service
                                                Account with a known set of roles. Each project, tenant, and environment can have exactly the permissions they require within a cluster.
                                            </p>
                                            <ExternalLink href="octopus-permissions-controller">Docs</ExternalLink>
                                        </div>}/>} note="For complex security requirements" value={props.useOctopusPermissionsController} onChange={(newValue) => props.onUseOctopusPermissionsControllerChanged(newValue)}/>
                    </div>)}

                {/* Storage Class */}
                <Text value={props.model.StorageClassName} onChange={(val) => {
            props.onModelChange({ StorageClassName: val });
            props.onUsePersistentVolumeStorageChanged(!StringHelper.isNullOrWhiteSpace(val));
        }} helperText={ConfigurationPageStorageClassHelperContent(EndpointSelectionScope.DeploymentTarget)} label="Storage class"/>

                {/* Local Development Server URL Helper */}
                {props.canModifyServerUrls && (<LocalDevServerUrlHelper serverNodeCommsAddresses={props.model.ServerNodeCommsAddresses} onServerAddressesChanged={(serverUri, grpcUri, serverNodeCommsAddresses) => props.onModelChange({
                ServerUri: serverUri,
                ServerNodeCommsAddresses: serverNodeCommsAddresses,
                ServerGrpcUri: grpcUri,
            })}/>)}

                {/* Server URLs (if modifiable) */}
                {props.canModifyServerUrls && (<>
                        <Text value={props.model.ServerUri} onChange={(ServerUri) => props.onModelChange({ ServerUri })} error={getFieldError("ServerUri")} label="Octopus Deploy Server URL" helperText={ConfigurationMessages.ServerUri.HelperText} validate={required(ConfigurationMessages.ServerUri.Validation.NotProvided)}/>
                        <Text value={props.model.ServerGrpcUri} onChange={(ServerGrpcUri) => props.onModelChange({ ServerGrpcUri })} error={getFieldError("ServerGrpcUri")} label="Octopus Deploy Server gRPC URL" helperText={ConfigurationMessages.ServerGrpcUri.HelperText} validate={required(ConfigurationMessages.ServerGrpcUri.Validation.NotProvided)}/>
                        {!props.requiresHaConfiguration && (<Text value={props.model.ServerNodeCommsAddresses[0]?.commsAddress || ""} onChange={(commsAddress) => props.onModelChange({ ServerNodeCommsAddresses: [{ nodeName: "", commsAddress }] })} error={getFieldError("ServerNodeCommsAddresses")} label="Octopus Deploy Server Communications URL" helperText={ConfigurationMessages.ServerCommsAddresses.HelperText} validate={required(ConfigurationMessages.ServerCommsAddresses.Validation.NotProvided)}/>)}
                    </>)}
            </div>
        </div>);
}
const styles = {
    sectionTitle: css({
        marginBottom: space[16],
        "& h3": {
            margin: 0,
            marginBottom: space[8],
        },
    }),
    sectionDescriptionStyles: css({
        margin: 0,
        color: themeTokens.color.text.secondary,
        font: text.body.regular.small,
    }),
    haCallout: css({
        paddingTop: space[8],
    }),
    inputWithHelp: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        paddingBottom: space[4],
        "&& :last-child": {
            bottom: 0,
        },
    }),
};
